/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.22.0
 */

import * as Yup from 'yup';

import type { IConnection } from '@/main/pages/PageAccountInfo/types/pageAccountInfoGeneralTypes';
import { stringFormatParams } from '@utils/stringFormat';

import EFormValidation from '../../form/const/formValidation';
import { ELocaleKeys } from '../../internationalization/intConstants';
import type { ICorporateFormTranslation } from '../../internationalization/types/corporate';
import { EEdmDeliveryInfoFields } from '../constants';
import type { IEdmOptionItem } from './../types';

export const setAddConnectionValidationSchema = (
  translation: ICorporateFormTranslation,
) => {
  const { validation } = translation;
  const phoneRegExp = new RegExp('^[0-9]{10}$');

  const requiredField = validation[EFormValidation.fieldRequired];
  const nameLongError = stringFormatParams(
    validation[EFormValidation.fieldNameLong],
    {
      [ELocaleKeys.number]: 100,
    },
  );
  const phoneFormatError = validation[EFormValidation.fieldPhoneCommonFormat];
  const emailFormatError = validation[EFormValidation.fieldEmailFormat];

  const operatorSchema = Yup.object({
    label: Yup.string(),
    value: Yup.number(),
  });
  const nameSchema = Yup.string().when(EEdmDeliveryInfoFields.operator, {
    is: (value: IEdmOptionItem) => value.isRoaming === false,
    then: Yup.string().notRequired(),
    otherwise: Yup.string()
      .trim()
      .max(100, nameLongError)
      .required(requiredField),
  });
  const phoneSchema = Yup.string().when(EEdmDeliveryInfoFields.operator, {
    is: (value: IEdmOptionItem) => value.isRoaming === false,
    then: Yup.string().notRequired(),
    otherwise: Yup.string()
      .matches(phoneRegExp, phoneFormatError)
      .required(requiredField),
  });
  const emailSchema = Yup.string().when(EEdmDeliveryInfoFields.operator, {
    is: (value: IEdmOptionItem) => value.isRoaming === false,
    then: Yup.string().notRequired(),
    otherwise: Yup.string().email(emailFormatError).required(requiredField),
  });

  return Yup.object().shape({
    [EEdmDeliveryInfoFields.operator]: operatorSchema,
    [EEdmDeliveryInfoFields.name]: nameSchema,
    [EEdmDeliveryInfoFields.phone]: phoneSchema,
    [EEdmDeliveryInfoFields.email]: emailSchema,
  });
};

export const setEditResponsibleValidationSchema = (
  connection: IConnection,
  translation: ICorporateFormTranslation,
) => {
  const { validation } = translation;
  const phoneRegExp = new RegExp('^[0-9]{10}$');
  const { allowEmailEdit, allowPersonNameEdit, allowPhoneNumberEdit } =
    connection;

  const requiredField = validation[EFormValidation.fieldRequired];
  const nameLongError = stringFormatParams(
    validation[EFormValidation.fieldNameLong],
    {
      [ELocaleKeys.number]: 100,
    },
  );
  const phoneFormatError = validation[EFormValidation.fieldPhoneCommonFormat];
  const emailFormatError = validation[EFormValidation.fieldEmailFormat];

  const nameSchema = Yup.string().max(100, nameLongError);
  const phoneSchema = Yup.string().matches(phoneRegExp, phoneFormatError);
  const emailSchema = Yup.string().email(emailFormatError);

  return Yup.object().shape({
    [EEdmDeliveryInfoFields.name]: allowPersonNameEdit
      ? nameSchema.required(requiredField)
      : nameSchema.notRequired(),
    [EEdmDeliveryInfoFields.phone]: allowPhoneNumberEdit
      ? phoneSchema.required(requiredField)
      : phoneSchema.notRequired(),
    [EEdmDeliveryInfoFields.email]: allowEmailEdit
      ? emailSchema.required(requiredField)
      : emailSchema.notRequired(),
  });
};
